import { browserSmsInfo, smsHeaders, isOtpRequest } from '../utils/sms-client-info'
import axios from 'axios'
import { requireSession, checkSession, readToken } from '@/utils/shared-session'
import { DataModel } from '../library/proto'
import { v4 as uuidv4 } from 'uuid'
import Swal from 'sweetalert2'

export const LoadingModels = {}
const storedAccessToken = () => {
  try {
    const token = JSON.parse(localStorage.getItem('token') || 'null')
    return token && typeof token.access_token === 'string' && /^Bearer\s+\S+$/i.test(token.access_token)
      ? token.access_token
      : ''
  } catch (error) {
    return ''
  }
}

const config = {
  api: {
    baseUrl: process.env.VUE_APP_BASE_URL,
    headers: {
      'X-Client-Version': process.env.VUE_APP_VERSION
    }
  }
}
export const axiosInstance = (param) => {
  const ops = param || {}
  const apiConfig = axios.create({
    baseURL: `${config.api.baseUrl}`,
    headers: config.api.headers,
    ...ops
  })

  const requestSuccess = async req => {
    if (isOtpRequest(req)) req.headers = { ...req.headers, ...smsHeaders(browserSmsInfo()) }
    req.url = (() => {
      const url = [req.url]
      if (ops.params && ops.params.length) {
        url.push('/' + ops.params.join('/'))
      }
      if (ops.query) {
        const query = Object.keys(ops.query).map(key => key + '=' + ops.query[key])
        url.push('?' + query.join('&'))
      }
      return url.join('')
    })()

    if (!req.skipSessionCheck && readToken() && !/login|refresh-token|password|register|otp/.test(req.url)) await requireSession({ background: true })
    const accessToken = storedAccessToken()
    if (localStorage.getItem('token') && !accessToken) {
      localStorage.removeItem('token')
      if (window.location.pathname !== '/member/login') {
        window.location.replace('/member/login')
      }
      const error = new Error('Invalid stored access token')
      error.response = { status: 401, data: { message: 'Unauthenticated.' } }
      return Promise.reject(error)
    }
    if (accessToken) {
      if (ops.model instanceof DataModel) {
        const uuid = uuidv4()
        ops.uuid = uuid
        ops.model.loaded = false
        LoadingModels[uuid] = ops.model
      }
      req.headers.Authorization = accessToken
      req.headers['Authorization-type'] = 'access'
    }
    return req
  }

  const requestError = err => Promise.reject(err)

  const responseSuccess = res => {
    if (res.config?.headers?.Authorization && res.config.headers.Authorization !== storedAccessToken()) {
      const error = new Error('登入已切換，忽略舊回應'); error.code = 'STALE_SESSION_RESPONSE'; return Promise.reject(error)
    }
    if (ops.uuid && LoadingModels[ops.uuid]) {
      const uuid = ops.uuid
      const model = LoadingModels[uuid]
      res.data = model.set({
        ...res.data,
        loaded: true
      })
      delete LoadingModels[ops.uuid]
    }
    return res
  }

  let warningShown = false

  const responseError = err => {
    if (err.config?.headers?.Authorization && err.config.headers.Authorization !== storedAccessToken()) return Promise.reject(err)
    const errorMessage =
      err &&
      err.response &&
      err.response.data &&
      err.response.data.message
        ? err.response.data.message
        : ''

    // 網路中斷不代表登入失效，交由呼叫頁面顯示重試提示。
    // 只有後端明確回覆未授權時，才清除會員登入資料。
    const isUnauthenticated =
      errorMessage === 'Unauthenticated.' ||
      Number(err && err.response && err.response.status) === 401

    if (['SESSION_EXPIRED', 'SESSION_REVOKED'].includes(err.response?.data?.error_code) || (isUnauthenticated && readToken()?.session_mode === 'shared')) {
      checkSession(true)
      return Promise.reject(err)
    }
    if (isUnauthenticated && localStorage.getItem('token')) {
      if (!warningShown) {
        warningShown = true
        Swal.fire({
          html: `
          <strong>
            您的會員帳號已由系統登出；<br />
            請點選確認回到首頁。
          </strong>
          `,
          icon: '',
          confirmButtonText: '確認',
          confirmButtonColor: '#3085d6',
          allowOutsideClick: false,
          allowEscapeKey: false
        }).then((result) => {
          if (result.isConfirmed) {
            if (err.config?.headers?.Authorization && err.config.headers.Authorization !== storedAccessToken()) return
            localStorage.removeItem('token')
            window.location.replace('/home')
            warningShown = false
          }
        })
      }
    }

    const hasRedirected = sessionStorage.getItem('hasRedirected') === 'true'

    if (errorMessage === '網站關閉維護中' && !hasRedirected) {
      sessionStorage.setItem('maintenanceDetected', 'true')
      sessionStorage.setItem('hasRedirected', 'true')
      window.location.href = '/error'
    }
    if (ops.uuid && LoadingModels[ops.uuid]) {
      delete LoadingModels[ops.uuid]
    }
    return Promise.reject(err)
  }

  apiConfig.interceptors.request.use(requestSuccess, requestError)
  apiConfig.interceptors.response.use(responseSuccess, responseError)

  return apiConfig
}

export const request = axiosInstance()

// Usage Example
// request.post('/some-endpoint', { data: 'example' })
//   .then(response => {
//     console.log('Success:', response)
//   })
//   .catch(error => {
//     console.error('Error:', error)
//   })
